import { TFunction } from 'i18next';
import WidgetCalculatorForm from './widget-calculator-form.component';
import { ParcelTab, ParcelTabContent } from './widget-calculator.styles';
import TrackingForm from './tracking-form.component';
import { ReactComponent as MeestShipmentsIcon } from '../../../assets/globus-icon.svg';
import { ReactComponent as PolonezShipmentsIcon } from '../../../assets/polonez-shipments-icon.svg';
import { ReactComponent as MagnifyingGlassIcon } from '../../../assets/magnifying-glass-icon.svg';
import { client } from '../../../config';
import { isFeatureRequired, isMeest } from '../../../utils/client/client.utils';

export type WidgetTabs = 'calculator' | 'domestic' | 'tracking';

interface TabConfig {
  name: 'calculator' | 'domestic' | 'tracking';
  isActive: boolean;
  tab: JSX.Element;
  element: JSX.Element | undefined;
}

const getIcon = () => {
  const icons = {
    meest: <MeestShipmentsIcon />,
    polonez: <PolonezShipmentsIcon />,
  };
  return icons[client];
};

const isMeestClient = isMeest(client);
export function getTabs(
  activeTab: WidgetTabs,
  setActiveTab: (activeTab: WidgetTabs) => void,
  t: TFunction<'translation', undefined>,
  domesticAllowed: boolean,
  domesticFlagImg: string | undefined
) {
  const tabs: TabConfig[] = [
    {
      name: 'calculator',
      get isActive() {
        return activeTab === this.name;
      },
      get tab() {
        return (
          <ParcelTab
            key='calculator'
            $isActive={this.isActive}
            onClick={(e) => {
              e.preventDefault();
              setActiveTab(this.name);
            }}>
            <ParcelTabContent>{getIcon()}</ParcelTabContent>
            <ParcelTabContent
              $fontWeight={isMeestClient ? '700' : '500'}
              $lineHeight={isMeestClient ? '1.6rem' : '2.2rem'}>
              {t('main_page_calculator_international_tab')}
            </ParcelTabContent>
          </ParcelTab>
        );
      },
      get element() {
        return this.isActive ? (
          <WidgetCalculatorForm
            key='calculator'
            useZipSearchForDeparture={isFeatureRequired(
              'calculatorUseZipDepartureSearch'
            )}
            useZipSearchForDestination={isFeatureRequired(
              'calculatorUseZipDestinationSearch'
            )}
          />
        ) : undefined;
      },
    },
    {
      name: 'tracking',
      get isActive() {
        return activeTab === this.name;
      },
      get tab() {
        return (
          <ParcelTab
            key='tracking'
            $isActive={this.isActive}
            onClick={(e) => {
              e.preventDefault();
              setActiveTab('tracking');
            }}>
            <ParcelTabContent>
              <MagnifyingGlassIcon />
            </ParcelTabContent>
            <ParcelTabContent>
              {t('main_page_calculator_tracking_tab')}
            </ParcelTabContent>
          </ParcelTab>
        );
      },
      get element() {
        return this.isActive ? <TrackingForm key='tracking' /> : undefined;
      },
    },
  ];

  const domesticTab: TabConfig = {
    name: 'domestic',
    get isActive() {
      return activeTab === this.name;
    },
    get tab() {
      return (
        <ParcelTab
          key='domestic'
          $isActive={this.isActive}
          onClick={(e) => {
            e.preventDefault();
            setActiveTab('domestic');
          }}>
          <ParcelTabContent>
            <img
              src={domesticFlagImg}
              alt='icon'
            />
          </ParcelTabContent>
          <ParcelTabContent>
            {t('main_page_calculator_domestic_tab')}
          </ParcelTabContent>
        </ParcelTab>
      );
    },
    get element() {
      return this.isActive ? (
        <WidgetCalculatorForm
          key='domestic'
          isDomesticFlow
          useZipSearchForDeparture
          useZipSearchForDestination
        />
      ) : undefined;
    },
  };

  if (domesticAllowed) {
    tabs.splice(1, 0, domesticTab);
  }

  return tabs;
}
